import { theme } from '@theme';
import { SuccessToast } from '@utils/ToastUtils';
import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { usePortfolioBulkActionsContainer } from '../../../../../hooks/UsePortfolioBulkActionsContainer';
import { BulkUpdateSendLeadsToCRMResource } from '../../../../../types/PortfolioBulkActionResource';
import { PortfolioResource } from '../../../../../types/PortfolioResource';
import ButtonComponent from '../../../../shared/button/ButtonComponent';
import ConfirmationModal from '../../../../shared/modals/confirmation-modal/ConfirmationModal';
import { Typography } from '../../../../style';

type SendLeadsToCRMProps = {
  selectedRecords: Array<PortfolioResource>;
  selectedRecordsCount: number;
  totalRecordsCount: number;
  isAllRowsSelected: boolean;
  isClearSelectionLink: boolean;
  toggleSelectedRows: (value: boolean) => void;
};

const SendLeadsToCRM: FC<SendLeadsToCRMProps> = (props) => {
  const [confirmModalOpen, setConfirmModalOpen] = useState<boolean>(false);
  const { createSendLeadsToCRMPayload, bulkSendLeadsToCRM } = usePortfolioBulkActionsContainer();
  const dispatch = useDispatch();

  const openSendLeadsToCRMModal = (): void => {
    setConfirmModalOpen(true);
  };

  const handleCancel = () => {
    dispatch(setConfirmModalOpen(false));
  };

  const handleSubmit = () => {
    let payload: BulkUpdateSendLeadsToCRMResource = createSendLeadsToCRMPayload(
      props.isAllRowsSelected,
      props.isClearSelectionLink,
      props.selectedRecords,
    );
    const successCallback = () => {
      props.toggleSelectedRows(false);
      SuccessToast('Processing send leads to CRM');
    };
    setConfirmModalOpen(false);
    bulkSendLeadsToCRM(payload, successCallback);
  };

  const bodyComponent: JSX.Element = (
    <>
      <Typography
        size={theme.typography.h4}
        display={'block'}
        fontWeight={theme.typography.fontWeights.semibold}
      >
        You are about to send{' '}
        {props.isClearSelectionLink && props.isAllRowsSelected
          ? props.totalRecordsCount
          : props.selectedRecordsCount}{' '}
        leads to the CRM
      </Typography>
      <Typography size={theme.typography.h5} paddingTop={10} display={'block'}>
        Are you sure?
      </Typography>
      <WarningContainer>
        <WarningBox>
          <WarningIcon />
          <Typography
            size={theme.typography.h5}
            paddingTop={10}
            backgroundColor={theme.palette.warningLight}
            color={theme.palette.warningText}
          >
            Warning: If you resend the previously sent lead, a new customer record will be created
            in your CRM.
          </Typography>
        </WarningBox>
      </WarningContainer>
    </>
  );

  return (
    <>
      <ButtonComponent
        color={theme.palette.mediumGray}
        wrapperBackgroundColor={theme.palette.blackOlive}
        text={'Send Leads to CRM...'}
        fontWeight={theme.typography.fontWeights.semibold}
        outerPadding={'0px'}
        innerPadding={'7px 12px'}
        borderRadius={4}
        onClick={openSendLeadsToCRMModal}
        textAlign='left'
      />
      <ConfirmationModal
        isOpen={confirmModalOpen}
        handleSubmitClick={handleSubmit}
        handleCancelClick={handleCancel}
        cancelButtonText='Cancel'
        submitButtonText='Send'
        title='Confirm Sending to CRM'
        bodyComponent={bodyComponent}
        showCloseIcon={false}
        padding={'20px 50px 20px 20px'}
        minHeight={'225px'}
      />
    </>
  );
};

export default SendLeadsToCRM;

export const WarningContainer = styled.div`
  margin-left: -20px;
  margin-right: -50px;
  margin-top: 10px;
  max-width: 475px;
  line-height: 1.8;
`;

export const WarningBox = styled.div`
  border-radius: 0;
  margin-bottom: 0;
  border-bottom: 0;
  border-left: 0;
  border-right: 0;
  background-color: #fff9dc;
  border-color: #c6beae;
  color: #8a6d3b;
  padding: 10px 30px 10px 45px;
  border-top: 1px solid #c6beae;
  border-bottom: 1px solid #e3ddd2;
`;
export const WarningIcon = styled.i.attrs({ className: 'am-icon am-icon-warning' })`
  position: absolute;
  top: 152px;
  left: 20px;
`;
